'use client';
// ──────────────────────────────────────────────────────
// DeviceGate — Full lifecycle device authorization gate
//
// States:
//   idle         → unprovisioned, show "send request" CTA
//   submitting   → sending request to backend
//   pending      → request exists, awaiting admin approval
//   claiming     → approval detected, claiming credentials
//   provisioned  → credentials stored, gate dismissing
//   rejected     → admin rejected request
//   expired      → claim token expired
//   revoked      → runtime credentials were invalid / cleared
//   error        → transient network/backend error
// ──────────────────────────────────────────────────────
import { useState, useEffect, useCallback, useRef, useSyncExternalStore } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getFingerprint, storeRuntimeCredentials } from '@/lib/kiosk-identity';

type GateState =
    | 'idle'
    | 'submitting'
    | 'pending'
    | 'claiming'
    | 'provisioned'
    | 'rejected'
    | 'expired'
    | 'revoked'
    | 'error';

const POLL_INTERVAL_MS = 10_000; // 10 seconds

// SSR-safe fingerprint store for useSyncExternalStore
const noopSubscribe = () => () => {};
const fpServerSnapshot = () => '';

interface DeviceGateProps {
    /** Called when device becomes provisioned — gate should close */
    onProvisioned?: () => void;
    /** Reason the gate is showing — used to select initial state */
    reason?: 'unprovisioned' | 'revoked';
}

export function DeviceGate({ onProvisioned, reason }: DeviceGateProps) {
    const [state, setState] = useState<GateState>(reason === 'revoked' ? 'revoked' : 'idle');
    // SSR-safe fingerprint (no setState in effect)
    const fingerprint = useSyncExternalStore(noopSubscribe, getFingerprint, fpServerSnapshot);
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // ── Status polling — runs while pending or idle ──────
    const checkStatus = useCallback(async () => {
        try {
            const res = await fetch(`/api/backend/admin/device-access/status/${encodeURIComponent(fingerprint)}`);
            if (!res.ok) return;
            const data = await res.json();

            if (data.status === 'approved' && data.claimToken) {
                // Approval detected — claim credentials
                setState('claiming');
                await claimCredentials(fingerprint, data.claimToken);
            } else if (data.status === 'approved' && !data.claimToken) {
                // Approved but no claim token — expired
                setState('expired');
            } else if (data.status === 'claimed') {
                // Already claimed (e.g. duplicate tab)
                setState('provisioned');
            } else if (data.status === 'pending') {
                setState('pending');
            } else if (data.status === 'rejected') {
                setState('rejected');
            }
        } catch {
            // Network error — don't change state, polling will retry
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fingerprint]);

    // ── Claim credentials ──────────────────────────────
    const claimCredentials = useCallback(async (fp: string, claimToken: string) => {
        try {
            const res = await fetch('/api/backend/admin/device-access/claim', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ fingerprint: fp, claimToken }),
            });

            if (!res.ok) {
                await res.json().catch(() => ({}));
                // Check if expired
                if (res.status === 409) {
                    setState('expired');
                } else {
                    setState('error');
                }
                return;
            }

            const data = await res.json();

            // Store credentials in localStorage
            storeRuntimeCredentials(data.kioskId, data.kioskKey);
            setState('provisioned');
        } catch {
            setState('error');
        }
    }, []);

    // ── Start/stop polling based on state ──────────────
    useEffect(() => {
        const shouldPoll = state === 'pending' || state === 'idle';

        if (shouldPoll) {
            // Initial check immediately
            checkStatus();
            pollRef.current = setInterval(checkStatus, POLL_INTERVAL_MS);
        } else {
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
        }

        return () => {
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
        };
    }, [state, checkStatus]);

    // ── Dismiss gate on provisioned ────────────────────
    useEffect(() => {
        if (state === 'provisioned') {
            const timer = setTimeout(() => onProvisioned?.(), 1500);
            return () => clearTimeout(timer);
        }
    }, [state, onProvisioned]);

    // ── Submit request ─────────────────────────────────
    const handleSubmit = useCallback(async () => {
        if (state === 'submitting') return;
        setState('submitting');
        try {
            const res = await fetch('/api/backend/admin/device-access/request', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    fingerprint,
                    userAgent: navigator.userAgent,
                }),
            });
            const data = await res.json();
            if (data.status === 'approved' && data.claimToken) {
                setState('claiming');
                await claimCredentials(fingerprint, data.claimToken);
            } else if (data.status === 'approved') {
                setState('expired');
            } else if (data.status === 'claimed') {
                setState('provisioned');
            } else {
                setState('pending');
            }
        } catch {
            setState('error');
        }
    }, [fingerprint, state, claimCredentials]);

    // ── Re-request (after rejection / expiry) ──────────
    const handleReRequest = useCallback(() => {
        // Go directly to submitting a new request instead of idle
        // (idle would just poll status, which returns the same expired/rejected state)
        handleSubmit();
    }, [handleSubmit]);

    // ── Retry from error ───────────────────────────────
    const handleRetry = useCallback(() => {
        setState('idle');
    }, []);

    // ── State → UI config ──────────────────────────────
    const stateConfig = getStateConfig(state, handleSubmit, handleReRequest, handleRetry);

    return (
        <div
            style={{
                position: 'fixed', inset: 0, zIndex: 9999,
                background: 'linear-gradient(135deg, #0f172a 0%, #071A30 60%, #0c1f35 100%)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                padding: '24px',
                fontFamily: "'Segoe UI', Tahoma, system-ui, sans-serif",
            }}
            dir="rtl"
        >
            <AnimatePresence mode="wait">
                <motion.div
                    key={state}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -16 }}
                    transition={{ duration: 0.4 }}
                    style={{ textAlign: 'center', maxWidth: 480, width: '100%' }}
                >
                    {/* Q Logo mark */}
                    <div style={{
                        width: 80, height: 80, borderRadius: '50%', margin: '0 auto 28px',
                        background: `linear-gradient(135deg, ${stateConfig.accentFrom}, ${stateConfig.accentTo})`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: `0 0 40px ${stateConfig.glow}`,
                    }}>
                        <span style={{ fontSize: 38, fontWeight: 900, color: '#fff', fontFamily: 'serif', lineHeight: 1 }}>Q</span>
                    </div>

                    {/* Icon */}
                    <div style={{ fontSize: 48, marginBottom: 16 }}>{stateConfig.icon}</div>

                    {/* Title */}
                    <h1 style={{
                        fontSize: 26, fontWeight: 800, color: '#f1f5f9',
                        marginBottom: 16, lineHeight: 1.4, letterSpacing: '-0.02em',
                    }}>
                        {stateConfig.title}
                    </h1>

                    {/* Body */}
                    <p style={{
                        fontSize: 15, color: '#94a3b8', lineHeight: 1.8,
                        marginBottom: 36, maxWidth: 380, margin: '0 auto 36px',
                    }}>
                        {stateConfig.body}
                    </p>

                    {/* Primary action */}
                    {stateConfig.primaryAction && (
                        <button
                            onClick={stateConfig.primaryAction.onClick}
                            disabled={stateConfig.primaryAction.disabled}
                            style={{
                                width: '100%', maxWidth: 320, padding: '16px 24px',
                                borderRadius: 14, border: 'none', cursor: 'pointer',
                                background: `linear-gradient(135deg, ${stateConfig.accentFrom}, ${stateConfig.accentTo})`,
                                color: '#fff', fontSize: 17, fontWeight: 700,
                                display: 'block', margin: '0 auto 14px',
                                boxShadow: `0 4px 20px ${stateConfig.glow}`,
                                opacity: stateConfig.primaryAction.disabled ? 0.7 : 1,
                                transition: 'all 0.2s',
                            }}
                        >
                            {stateConfig.primaryAction.label}
                        </button>
                    )}

                    {/* Secondary action */}
                    {stateConfig.secondaryAction && (
                        <button
                            onClick={stateConfig.secondaryAction.onClick}
                            style={{
                                background: 'transparent', border: '1px solid rgba(148,163,184,0.3)',
                                color: '#64748b', borderRadius: 10, padding: '10px 24px',
                                fontSize: 14, cursor: 'pointer', display: 'block', margin: '0 auto',
                            }}
                        >
                            {stateConfig.secondaryAction.label}
                        </button>
                    )}

                    {/* Spinner for pending/claiming */}
                    {(state === 'pending' || state === 'claiming') && (
                        <div style={{ marginTop: 24, opacity: 0.5 }}>
                            <div style={{
                                width: 32, height: 32, border: '3px solid rgba(255,255,255,0.15)',
                                borderTopColor: '#0ea5a5', borderRadius: '50%',
                                animation: 'spin 1s linear infinite',
                                margin: '0 auto',
                            }} />
                            <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
                            <p style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>
                                {state === 'pending' ? 'يتم التحقق تلقائيًا كل ١٠ ثوانٍ...' : 'جارٍ تجهيز الجهاز...'}
                            </p>
                        </div>
                    )}
                </motion.div>
            </AnimatePresence>
        </div>
    );
}

// ── State configuration map ────────────────────────────

interface StateAction {
    label: string;
    onClick: () => void;
    disabled?: boolean;
}

interface StateConfig {
    icon: string;
    title: string;
    body: string;
    accentFrom: string;
    accentTo: string;
    glow: string;
    primaryAction?: StateAction;
    secondaryAction?: StateAction;
}

function getStateConfig(
    state: GateState,
    onSubmit: () => void,
    onReRequest: () => void,
    onRetry: () => void,
): StateConfig {
    switch (state) {
        case 'idle':
            return {
                icon: '🔒',
                title: 'هذا الجهاز غير معتمد حاليًا',
                body: 'هذا الجهاز غير معتمد للوصول إلى نظام Q.\nللمتابعة، يرجى إرسال طلب اعتماد إلى المسؤول.\nسيتم مراجعة الطلب وتفعيل الجهاز بعد الموافقة.',
                accentFrom: '#0ea5a5', accentTo: '#0c7a7a', glow: 'rgba(14,165,165,0.4)',
                primaryAction: { label: '📤 إرسال طلب اعتماد', onClick: onSubmit },
            };
        case 'submitting':
            return {
                icon: '⏳',
                title: 'جارٍ إرسال الطلب...',
                body: 'يتم إرسال طلب الاعتماد إلى النظام...',
                accentFrom: '#0ea5a5', accentTo: '#0c7a7a', glow: 'rgba(14,165,165,0.4)',
                primaryAction: { label: '⏳ جارٍ الإرسال...', onClick: () => {}, disabled: true },
            };
        case 'pending':
            return {
                icon: '⏳',
                title: 'طلبك قيد المراجعة',
                body: 'تم إرسال طلب اعتماد هذا الجهاز بنجاح.\nسيتم تفعيل الجهاز تلقائيًا فور موافقة المسؤول.\nلا حاجة لأي إجراء إضافي من طرفك.',
                accentFrom: '#f59e0b', accentTo: '#d97706', glow: 'rgba(245,158,11,0.3)',
            };
        case 'claiming':
            return {
                icon: '⚙️',
                title: 'تمت الموافقة — جارٍ تجهيز الجهاز',
                body: 'تمت الموافقة على هذا الجهاز.\nيتم الآن تجهيز بيانات الاعتماد الخاصة به...',
                accentFrom: '#0ea5a5', accentTo: '#0c7a7a', glow: 'rgba(14,165,165,0.4)',
            };
        case 'provisioned':
            return {
                icon: '✅',
                title: 'الجهاز جاهز للعمل',
                body: 'تم اعتماد الجهاز وتفعيله بنجاح.\nجارٍ فتح واجهة Q...',
                accentFrom: '#10b981', accentTo: '#059669', glow: 'rgba(16,185,129,0.4)',
            };
        case 'rejected':
            return {
                icon: '🚫',
                title: 'تم رفض الطلب',
                body: 'قام المسؤول برفض طلب اعتماد هذا الجهاز.\nإذا كنت تعتقد أن هذا خطأ، يمكنك إعادة الطلب.',
                accentFrom: '#ef4444', accentTo: '#dc2626', glow: 'rgba(239,68,68,0.3)',
                primaryAction: { label: '🔄 إعادة طلب الاعتماد', onClick: onReRequest },
            };
        case 'expired':
            return {
                icon: '⏰',
                title: 'انتهت صلاحية الاعتماد',
                body: 'تمت الموافقة على هذا الجهاز سابقًا، لكن لم يتم استلام البيانات في الوقت المحدد.\nيرجى طلب اعتماد جديد.',
                accentFrom: '#f59e0b', accentTo: '#d97706', glow: 'rgba(245,158,11,0.3)',
                primaryAction: { label: '🔄 إعادة طلب الاعتماد', onClick: onReRequest },
            };
        case 'revoked':
            return {
                icon: '🔓',
                title: 'الجهاز يحتاج إعادة اعتماد',
                body: 'تم إلغاء اعتماد هذا الجهاز أو أصبحت بياناته غير صالحة.\nيرجى إرسال طلب اعتماد جديد للمتابعة.',
                accentFrom: '#ef4444', accentTo: '#dc2626', glow: 'rgba(239,68,68,0.3)',
                primaryAction: { label: '📤 إرسال طلب اعتماد جديد', onClick: onSubmit },
            };
        case 'error':
            return {
                icon: '⚠️',
                title: 'مشكلة مؤقتة في الاتصال',
                body: 'لم نتمكن من الاتصال بالنظام حاليًا.\nيرجى المحاولة مرة أخرى بعد لحظات.',
                accentFrom: '#64748b', accentTo: '#475569', glow: 'rgba(100,116,139,0.3)',
                primaryAction: { label: '🔄 إعادة المحاولة', onClick: onRetry },
            };
    }
}
